import React, { useMemo, useState } from "react";
import "./InstagramFollowAudit.css";

function csvEscape(value) {
  const s = String(value ?? "");
  return /[\",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function InstagramFollowAudit({ open, onClose, apiFetch, apiBase = "" }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("not_following_back");
  const [query, setQuery] = useState("");
  const [reviewed, setReviewed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("bamboo:instagram-reviewed") || "[]")); }
    catch { return new Set(); }
  });

  const rows = useMemo(() => {
    const source = Array.isArray(data?.[tab]) ? data[tab] : [];
    const q = query.trim().toLowerCase();
    return source.filter((row) => !q || String(row?.username || "").toLowerCase().includes(q));
  }, [data, tab, query]);

  if (!open) return null;

  const persistReviewed = (next) => {
    setReviewed(next);
    try { localStorage.setItem("bamboo:instagram-reviewed", JSON.stringify([...next])); } catch {}
  };

  const toggleReviewed = (username) => {
    const next = new Set(reviewed);
    if (next.has(username)) next.delete(username); else next.add(username);
    persistReviewed(next);
  };

  const runCompare = async () => {
    if (!files.length) { setError("Choose at least one Instagram export ZIP."); return; }
    setBusy(true); setError(""); setData(null);
    try {
      const form = new FormData();
      [...files].forEach((file) => form.append("files", file, file.name));
      let result;
      if (typeof apiFetch === "function") {
        result = await apiFetch("/instagram-follow-audit/compare", { method: "POST", body: form });
      } else {
        const r = await fetch(`${apiBase}/instagram-follow-audit/compare`, { method: "POST", credentials: "include", body: form });
        const txt = await r.text();
        let parsed = null; try { parsed = txt ? JSON.parse(txt) : null; } catch {}
        if (!r.ok) throw new Error(parsed?.detail || txt || `Request failed (${r.status})`);
        result = parsed;
      }
      setData(result);
      setTab("not_following_back");
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const source = Array.isArray(data?.[tab]) ? data[tab] : [];
    const csv = ["username,profile_url,reviewed", ...source.map((r) => [r.username, r.profile_url, reviewed.has(r.username) ? "yes" : "no"].map(csvEscape).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `instagram_${tab}.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const summary = data?.summary || {};
  return (
    <div className="ig-audit-overlay" onClick={() => !busy && onClose?.()}>
      <section className="ig-audit-card" onClick={(e) => e.stopPropagation()} aria-label="Instagram Follow Audit">
        <header className="ig-audit-head">
          <div><div className="ig-kicker">BAMBOO SOCIAL TOOLS</div><h2>Instagram Follow Audit</h2><p>Compare your official Instagram export safely. No password or Instagram login is required.</p></div>
          <button className="ig-close" onClick={() => !busy && onClose?.()} disabled={busy}>✕</button>
        </header>

        <div className="ig-upload-row">
          <label className="ig-drop">
            <input type="file" accept=".zip,application/zip" multiple onChange={(e) => setFiles(e.target.files || [])} />
            <span className="ig-drop-icon">⇧</span><strong>Choose Instagram export ZIP</strong><small>{files.length ? `${files.length} file(s) selected` : "HTML and JSON exports are both supported"}</small>
          </label>
          <button className="ig-primary" onClick={runCompare} disabled={busy || !files.length}>{busy ? "Comparing…" : "Compare Followers"}</button>
        </div>

        {error && <div className="ig-error">{error}</div>}

        {data && <>
          <div className="ig-stats">
            <div><small>Followers</small><strong>{Number(summary.followers || 0).toLocaleString()}</strong></div>
            <div><small>Following</small><strong>{Number(summary.following || 0).toLocaleString()}</strong></div>
            <div><small>Mutual</small><strong>{Number(summary.mutual || 0).toLocaleString()}</strong></div>
            <div className="danger"><small>Not following you back</small><strong>{Number(summary.not_following_back || 0).toLocaleString()}</strong></div>
          </div>

          <div className="ig-tabs">
            <button className={tab === "not_following_back" ? "active" : ""} onClick={() => setTab("not_following_back")}>Not following back ({summary.not_following_back || 0})</button>
            <button className={tab === "mutual" ? "active" : ""} onClick={() => setTab("mutual")}>Mutual ({summary.mutual || 0})</button>
            <button className={tab === "followers_not_followed" ? "active" : ""} onClick={() => setTab("followers_not_followed")}>Followers I don't follow ({summary.followers_not_followed || 0})</button>
          </div>

          <div className="ig-tools"><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search username…" /><button onClick={exportCsv}>Export CSV</button></div>
          <div className="ig-warning">For account safety, this tool does not run bulk-unfollow automation. Use <b>Open profile</b>, unfollow in Instagram, then mark the row reviewed.</div>

          <div className="ig-list">
            {rows.length === 0 ? <div className="ig-empty">No accounts match this view.</div> : rows.map((row) => (
              <div className={`ig-row ${reviewed.has(row.username) ? "reviewed" : ""}`} key={row.username}>
                <div className="ig-avatar">@</div><div className="ig-user"><strong>@{row.username}</strong><small>{reviewed.has(row.username) ? "Reviewed" : "Pending review"}</small></div>
                <a href={row.profile_url} target="_blank" rel="noreferrer" className="ig-open">Open profile ↗</a>
                <button className="ig-review" onClick={() => toggleReviewed(row.username)}>{reviewed.has(row.username) ? "Undo" : "Mark reviewed"}</button>
              </div>
            ))}
          </div>
        </>}
      </section>
    </div>
  );
}
